import api from "@/lib/api";
import type {
  Customer,
  CustomerDetail,
  ServerLockTime,
} from "@shared_types/customer";
import type {
  BankAccountCreate,
  EmailLogin,
  TaxInvoiceCreate,
  TransactionSearch,
} from "@validation/customer";
import type { GetPaymentsHisResponse } from "../documents/documents.types";
import type { AxiosRequestConfig } from "axios";
import axios from "axios";
import MD5 from "crypto-js/md5";

export const getUserDetail = async () => {
  const { data } = await api.get<Customer>("/customers");
  return data;
};

export const getUserWithPaymentContext = async () => {
  const { data } = await api.get<CustomerDetail>("/customers/payment-detail");
  return data;
};

export async function getCustomerTransactionHistory(filter: TransactionSearch) {
  const { data } = await api.get<GetPaymentsHisResponse>(
    "/customers/payment-history",
    {
      params: filter,
      paramsSerializer: {
        serialize: (params) => {
          const searchParams = new URLSearchParams();
          for (const key in params) {
            const value = params[key];
            if (Array.isArray(value)) {
              value.forEach((item) =>
                searchParams.append(`${key}`, String(item)),
              );
            } else if (value !== undefined && value !== null) {
              searchParams.append(key, String(value));
            }
          }
          return searchParams.toString();
        },
      },
    },
  );
  return data;
}

export async function getSettingsDetail() {
  const { data } = await api.get<CustomerDetail>("/customers/settings");
  return data;
}

export async function getUserServerLock() {
  const { data } = await api.get<ServerLockTime>("/customers/server-time");
  return data;
}

// Login
export async function loginEmail(data: EmailLogin) {
  try {
    const config: AxiosRequestConfig = {
      method: "post",
      url: `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/auth/login/email`,
      data: {
        ...data,
        password: MD5(data.password).toString(),
      },
    };
    const response = await axios(config);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    } else {
      throw new Error(`HTTP error: ${response.status}`);
    }
  } catch (error) {
    console.error("Error create session:", error);
    throw error;
  }
}
export async function exchangeToken(oneTimeCode: string) {
  try {
    const config: AxiosRequestConfig = {
      method: "post",
      url: `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/auth/token`,
      data: {
        code: oneTimeCode.toString(),
      },
    };
    const response = await axios(config);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    } else {
      throw new Error(`HTTP error: ${response.status}`);
    }
  } catch (error) {
    console.error("Error create session:", error);
    throw error;
  }
}

// Credit Card
export const createCreditCard = async (cardToken: string) => {
  const { data } = await api.post("/customers/credit-card", {
    cardToken,
  });
  return data;
};
export const deleteCreditCard = async (cardId: string) => {
  const { data } = await api.patch("/customers/credit-card", {
    cardId,
  });
  return data;
};

// Refund Bank
export async function deleteRefundBank(id: number) {
  const { data } = await api.patch("/customers/refundBank/del", {
    id,
  });
  return data;
}
export async function upsertRefund(body: BankAccountCreate) {
  const { data } = await api.post("/customers/refundBank", {
    ...body,
  });
  return data;
}

// TaxInvoice
export async function upsertTaxInvoice(body: TaxInvoiceCreate) {
  const { data } = await api.post("/customers/taxInvoice", {
    ...body,
  });
  return data;
}
export async function deleteTaxInvoice(id: number) {
  const { data } = await api.patch("/customers/taxInvoice/del", {
    id,
  });
  return data;
}

// Notify Paid Pickup
export async function notifyPaidPickup(pickupId: string) {
  const { data } = await api.post("/customers/paid-pickup", {
    pickupId,
  });
  return data;
}
